import React, { useEffect, useState, useRef } from 'react';
import './Style/Result.css';
import Loading from '../../Common/Loading';

interface ResultData {
  coview: string;
  studentFirstName: string;
  activityCompletionDate: string;
  activityTitle: string;
  unitName: string;
  chapterName: string;
  score: number;
  noCorrectAnswers: number;
  totalQuestions: number;
  pointsEarned: number;
  animationImage: string;
  avatarImage: string;
  resultTitle: string;
  resultComment: string;
  suggestion: string;
  message: string;
  showSolutionInfo: boolean;
  fullSolutionViewed: number;
  partialSolutionViewed: number;
  canEarnPoints: string;
}

const Result: React.FC = () => {
  const [resultData, setResultData] = useState<ResultData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scoreBarWidth, setScoreBarWidth] = useState(0);
  const [displayScore, setDisplayScore] = useState(0);
  const [showContinue, setShowContinue] = useState(false);

  const ChapterName = sessionStorage.getItem("ChapterName");
  const ActivityName = sessionStorage.getItem("ActivityName");
  const UnitName = sessionStorage.getItem("UnitName");
  const apiUrl = process.env.REACT_APP_DOCUMENT_LINK ;

  const divLoadingRef = useRef<HTMLDivElement | null>(null);
  const resultAvatarRef = useRef<HTMLImageElement | null>(null);
  const scoreBarRef = useRef<HTMLDivElement | null>(null);

  // Get URL parameters
  const getUrlParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      DICCode: params.get('DicCode') || params.get('DICCode') || '',
      LsnTestCode: params.get('LsnTestCode') || '',
      SessionID: params.get('SessionID') || '',
      CalledFrom: params.get('calledFrom') || '',
      RedirectTo: params.get('RedirectTo') || '',
      source: params.get('source') || 'lesson',
    };
  };

  useEffect(() => {
    const resultParams = getUrlParams();

    const fetchResultData = async () => {
      try {
        setLoading(true);
        const schoolCode = sessionStorage.getItem('SchoolCode') || '';
        const familyCode = sessionStorage.getItem('FamilyCode') || '';
        const login = sessionStorage.getItem('Login') || '';
        const urlParams = getUrlParams();
        const dicCodeParam = urlParams.DICCode;
        const lsnTestCodeParam = urlParams.LsnTestCode;
        const sessionIdParam = urlParams.SessionID || '1';

        if (!schoolCode || !familyCode || !login) {
          window.location.href = '/Common/EL_SessionExpiredNew.aspx';
          return;
        }

        if (!dicCodeParam || !lsnTestCodeParam) {
          setError("Test information not found in URL.");
          setLoading(false);
          return;
        }

        const queryParams = new URLSearchParams(window.location.search);
        if (urlParams.source === 'lesson' && queryParams.get('complete') === '1') {
          if (!urlParams.SessionID) {
            throw new Error('Lesson session identifier not found.');
          }

          const accessToken = localStorage.getItem('access_token');
          if (!accessToken) {
            throw new Error('Authentication token not found.');
          }

          const completeResponse = await fetch(`${apiUrl}complete_lesson_session/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              SchoolCode: schoolCode,
              FamilyCode: familyCode,
              Login: login,
              DICCode: dicCodeParam,
              LsnTestCode: lsnTestCodeParam,
              SessionID: urlParams.SessionID,
            }),
          });
          const completionData = await completeResponse.json();
          if (!completeResponse.ok) {
            throw new Error(completionData.message || 'Failed to complete the lesson.');
          }

          const completionKey =
            `completedLesson:${dicCodeParam}:${lsnTestCodeParam}`;
          localStorage.removeItem(completionKey);
          sessionStorage.removeItem(completionKey);
        }

        const response = await fetch(`${apiUrl}result/get-data/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            SchoolCode: schoolCode,
            FamilyCode: familyCode,
            Login: login,
            CurrentAvatar: sessionStorage.getItem('CurrentAvatar') || '',
            DICCode: dicCodeParam,
            LsnTestCode: lsnTestCodeParam,
            SessionID: sessionIdParam,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          setError(data.error || 'Failed to load result data');
          return;
        }

        if (data.success) {
          setResultData(data.data);

          if (data.data.coview === 'Y') {
            const currentUrl = window.location.href;
            if (!currentUrl.includes('coview-screenshare=1')) {
              window.location.href = currentUrl + '&coview-screenshare=1';
              return;
            }
          }

          setTimeout(() => {
            setScoreBarWidth(data.data.score);
          }, 300);
        }
      } catch (err) {
        console.error('Error fetching result data:', err);
        setError(
          err instanceof Error
            ? err.message
            : 'An error occurred while loading the results',
        );
      } finally {
        setLoading(false);
      }
    };

    fetchResultData();
  }, []);

  useEffect(() => {
    if (!resultData) return;

    const targetScore = Math.max(0, Math.min(100, resultData.score || 0));
    const startedAt = performance.now();
    let frameId = 0;
    let continueTimer: number | undefined;
    let confettiFrameId = 0;

    const launchConfetti = () => {
      if (targetScore !== 100 || !resultAvatarRef.current) return;

      const rect = resultAvatarRef.current.getBoundingClientRect();
      const originX = rect.left + rect.width / 2;
      const originY = rect.top + rect.height / 2 + 130;
      const particles: Array<{
        element: HTMLDivElement;
        x: number;
        y: number;
        vx: number;
        vy: number;
        rotation: number;
        rotationVelocity: number;
      }> = [];

      for (let index = 0; index < 40; index += 1) {
        const element = document.createElement('div');
        element.className = 'confetti';
        element.style.left = `${originX}px`;
        element.style.top = `${originY}px`;
        document.body.appendChild(element);

        const angle = Math.random() * Math.PI;
        const speed = 8 + Math.random() * 8;
        particles.push({
          element,
          x: originX,
          y: originY,
          vx: Math.cos(angle) * speed,
          vy: -Math.abs(Math.sin(angle) * speed) - 6,
          rotation: Math.random() * 360,
          rotationVelocity: (Math.random() - 0.5) * 10,
        });
      }

      const animateConfetti = () => {
        for (let index = particles.length - 1; index >= 0; index -= 1) {
          const particle = particles[index];
          particle.vy += 0.4;
          particle.x += particle.vx;
          particle.y += particle.vy;
          particle.rotation += particle.rotationVelocity;
          particle.element.style.left = `${particle.x}px`;
          particle.element.style.top = `${particle.y}px`;
          particle.element.style.transform = `translate(-50%, -50%) rotate(${particle.rotation}deg)`;
          particle.element.style.opacity = `${Math.max(0, 1 - particle.y / window.innerHeight)}`;

          if (particle.y > window.innerHeight + 20) {
            particle.element.remove();
            particles.splice(index, 1);
          }
        }

        if (particles.length > 0) {
          confettiFrameId = requestAnimationFrame(animateConfetti);
        }
      };

      confettiFrameId = requestAnimationFrame(animateConfetti);
    };

    const animateScore = (now: number) => {
      const progress = Math.min((now - startedAt) / 1000, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const nextScore = Math.floor(eased * targetScore);
      setDisplayScore(nextScore);
      setScoreBarWidth(nextScore);

      if (progress < 1) {
        frameId = requestAnimationFrame(animateScore);
      } else {
        setDisplayScore(targetScore);
        setScoreBarWidth(targetScore);
        continueTimer = window.setTimeout(() => setShowContinue(true), 1000);
        launchConfetti();
      }
    };

    frameId = requestAnimationFrame(animateScore);
    return () => {
      cancelAnimationFrame(frameId);
      if (continueTimer) window.clearTimeout(continueTimer);
      cancelAnimationFrame(confettiFrameId);
      document.querySelectorAll('.confetti').forEach((element) => element.remove());
    };
  }, [resultData]);

  const handleContinue = () => {
    try {
      if (divLoadingRef.current) {
        divLoadingRef.current.style.display = "block";
      }

      const urlParams = getUrlParams();
      const dicCode = urlParams.DICCode;
      const lsnTestCode = urlParams.LsnTestCode;
      const sessionId = urlParams.SessionID;
      const source = urlParams.source;
      let calledFrom = urlParams.CalledFrom; // Change const to let

      if (source === 'test') {
        if (!dicCode || !lsnTestCode || !sessionId) {
          alert("Error: Missing test information to show answer sheet.");
          return;
        }
        window.location.href = `/STUDENT/Tutorials/AnswerSheet?DicCode=${dicCode}&LsnTestCode=${lsnTestCode}&SessionID=${sessionId}&WhichQuestion=All` +
          (calledFrom ? `&calledFrom=${encodeURIComponent(calledFrom)}` : "");
      } else {
        if (calledFrom) {
          if (
            calledFrom.includes("StudyRoom") ||
            calledFrom.includes("ExploreActivities")
          ) {
            const separator = calledFrom.includes("?") ? "&" : "?";
            calledFrom = `${calledFrom}${separator}lastPage=Result`;
          }
          window.location.href = calledFrom;
        } else {
          window.location.href = '/STUDENT/MSR/ExploreActivities';
        }
      }

      sessionStorage.removeItem("UnitName");
      sessionStorage.removeItem("ActivityName");
      sessionStorage.removeItem("ChapterName");
      sessionStorage.removeItem("UnitCode");
      sessionStorage.removeItem("ChapterCode");
    } catch (err) {
      console.error('Error handling continue:', err);
      alert('An error occurred. Please try again.');
    }
  };

  const handlePrint = () => window.print();

  const assetBaseUrl = (apiUrl || window.location.origin).replace(/\/api\/?$/, '');
  const avatarUrl = resultData?.avatarImage?.startsWith('http')
    ? resultData.avatarImage
    : `${assetBaseUrl}${resultData?.avatarImage || ''}`;
  const fallbackAvatarUrl = `${assetBaseUrl}/Storage/assets/AvatarNew/Result_Avatar/template_image.png`;


  if (loading) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 99999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loading />
      </div>
    );
  }


  if (error || !resultData) {
    return (
      <div className="error-message">
        <p>{error || 'Failed to load results'}</p>
      </div>
    );
  }

  return (
    <>
      <div
        className="result-page"
      >
        <div id="divLoading" ref={divLoadingRef} style={{ display: "none" }}></div>

        <main id="results" className="section_container result-content">
          <section className="result-summary">
            <img
              id="divAnimationBox"
              className="result-avatar"
              ref={resultAvatarRef}
              src={avatarUrl}
              alt=""
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = fallbackAvatarUrl;
              }}
            />
            <div id="points_earned_div" className="result-points">
              <div className="points_decor left" aria-hidden="true">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <p className="points_earned_text">
                <span className="points_earned_text_large" id="spanPointsEarnedForActivity">{resultData.pointsEarned}</span> POINTS EARNED
              </p>
              <div className="points_decor right" aria-hidden="true">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
            <p id="pActivitySuggestion" className="result-title">{resultData.resultTitle || resultData.suggestion}</p>
            <p id="pActivityMessage" className="result-comment">{resultData.resultComment || resultData.message}</p>
          </section>

          <div className="result-score-card">
            <p className="result-activity">{resultData.activityTitle || ActivityName}</p>
            <section id="score_section_container" aria-label="Activity score">
              <div className="result-ratio">
                <span id="spanNoOfCorrectAnswers">{resultData.noCorrectAnswers}</span>&nbsp;/&nbsp;<span id="spanTotalNoOfQuestions">{resultData.totalQuestions}</span>
              </div>
              <div id="score_bar_container">
                <div id="score_bar" ref={scoreBarRef} style={{ width: `${scoreBarWidth}%` }}></div>
              </div>
              <div id="activity_score" style={{ fontWeight:'lighter' }} >
                <p><span id="spanActivityScorePercentage">{displayScore}</span>%</p>
              </div>
            </section>
          </div>

          {resultData.showSolutionInfo && (
            <p id="pIsSolutionSeen" className="result-solution-info">
              Viewed full solution: <span id="spanNoOfFullSolutionViewedQuestions">{resultData.fullSolutionViewed}</span> /
              Viewed partial solution: <span id="spanNoOfPartialSolutionViewedQuestions">{resultData.partialSolutionViewed}</span>
            </p>
          )}

          <div id="bottom_container">
            <div id="button_box">
              <div id="continue_btn"  className={`general_btn ${showContinue ? 'show' : ''}`}>
                <a onClick={handleContinue} style={{display:'flex', flexDirection:'column', justifyContent:'space-around', fontSize:'medium', cursor:'pointer'}}>Continue</a>
                <span id="ctn_arrow" aria-hidden="true"></span>
              </div>
              {/* Uncomment if print button needed */}
              {/* <div id="print_btn" className="custom_print_btn">
                <a onClick={handlePrint} style={{ cursor: 'pointer' }}>Print</a>
              </div> */}
            </div>
          </div>

          <p className="result-footer">
            <span>{resultData.studentFirstName}</span> / <span>{resultData.activityCompletionDate}</span>
          </p>
        </main>

      </div>
    </>
  );
};

export default Result;
